/**
 * Web Push subscription helpers.
 *
 * Usage:
 *   - hasNeverAsked() / markAsked() — track first-time prompt state
 *   - subscribeToPush(authAxios) — request permission + subscribe + POST to backend
 *   - unsubscribeFromPush(authAxios) — opposite
 *   - isPushSupported() — feature detect (covers iOS Safari < 16.4)
 */
const ASKED_KEY = "triolo_push_asked";

export function isPushSupported() {
  return (
    typeof window !== "undefined"
    && "Notification" in window
    && "serviceWorker" in navigator
    && "PushManager" in window
  );
}

export function hasNeverAsked() {
  try {
    return !localStorage.getItem(ASKED_KEY);
  } catch {
    return false;
  }
}

export function markAsked() {
  try { localStorage.setItem(ASKED_KEY, "1"); } catch (e) { console.debug("[push] markAsked failed:", e?.message); }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export async function subscribeToPush(authAxios) {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };

  // Pre-fetch the VAPID key BEFORE asking permission, so when the user grants
  // we go straight to subscribe() in the same gesture (iOS requires this).
  let publicKey = null;
  try {
    const { data } = await authAxios.get("/push/vapid-public-key");
    publicKey = data.public_key;
  } catch (e) {
    return { ok: false, reason: "vapid_unavailable", error: e.message };
  }

  // Se il permesso è già concesso non lo richiedo di nuovo: su iOS/PWA
  // requestPermission() può risolvere `undefined` e sembrare un rifiuto.
  let permission = Notification.permission;
  if (permission !== "granted") {
    permission = (await Notification.requestPermission()) || Notification.permission;
  }
  markAsked();
  if (permission !== "granted") return { ok: false, reason: "denied" };

  let reg;
  try {
    reg = await navigator.serviceWorker.ready;
  } catch (e) {
    return { ok: false, reason: "no_service_worker", error: e.message };
  }

  let sub;
  try {
    sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
  } catch (e) {
    // iOS sometimes throws InvalidStateError if existing sub has different VAPID key.
    // Workaround: unsubscribe and retry.
    try {
      const old = await reg.pushManager.getSubscription();
      if (old) await old.unsubscribe();
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (e2) {
      return { ok: false, reason: "subscribe_failed", error: e2.message || e.message };
    }
  }

  const json = sub.toJSON();
  try {
    await authAxios.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      expirationTime: json.expirationTime || null,
    });
  } catch (e) {
    return { ok: false, reason: "save_failed", error: e.response?.data?.detail || e.message };
  }
  return { ok: true };
}

export async function unsubscribeFromPush(authAxios) {
  if (!isPushSupported()) return { ok: false };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await authAxios.delete("/push/unsubscribe", { data: { endpoint: sub.endpoint } }); } catch (e) { console.debug("[push] unsubscribe API call failed:", e?.message); }
    await sub.unsubscribe();
  }
  return { ok: true };
}
